import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
} from "@tanstack/react-table";
import { Formik } from "formik";
import { components, OptionProps } from "react-select";
import "./dashboard.scss";
import "../terminal-selector/terminalSelector.scss";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import ContentContainer from "templates/content-container/contentContainer";
import { useDashboard } from "./hooks/useDashboard";
import { setSelectedWeekStartDate, staticLoadSelector } from "slices/static-load/staticLoadSlice";
import { useDispatch, useSelector } from "react-redux";
import { IState } from "slices";
import { useWeeks } from "./hooks/useWeek";
import { DashboardLaneEntry, DashboardPlan, DashboardResponse } from "app/data/static-load/model";
import Loading from "ui-components/loading/loading";
import { Dispatch, DispatchChip } from "./components/dispatchBoard";
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import XGSToggle from "ui-components/xgs-toggle/xgs-toggle";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import moment from "moment";
import { userSelector } from "slices/user/userSlice";
import { UserUtils } from "app/data/user/userUtils";
import {
  getRegionsServiceCenters,
  serviceCentersSelector,
} from "slices/service-centers/serviceCentersSlice";

const LINEHAUL_ADMINS_GROUP = "Linehaul Admins";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
type DayOfWeekKey = "1" | "2" | "3" | "4" | "5" | "6" | "7";

type RowData = {
  id: string;
  origin: string;
  destination: string;
  originCode: string;
  destinationCode: string;
  stopCodes: string[];
  monDispatches: number;
  tueDispatches: number;
  wedDispatches: number;
  thuDispatches: number;
  friDispatches: number;
  satDispatches: number;
  sunDispatches: number;
  destinationName: string;
  originName: string;
  plansByDay: Record<DayKey, DashboardPlan[]>;
  stops?: string | undefined;
};

type DispatchField =
  | "monDispatches"
  | "tueDispatches"
  | "wedDispatches"
  | "thuDispatches"
  | "friDispatches"
  | "satDispatches"
  | "sunDispatches";

const DAY_OF_WEEK_TO_FIELD: Record<DayOfWeekKey, DispatchField> = {
  "1": "monDispatches",
  "2": "tueDispatches",
  "3": "wedDispatches",
  "4": "thuDispatches",
  "5": "friDispatches",
  "6": "satDispatches",
  "7": "sunDispatches",
};

const DAY_OF_WEEK_TO_DAY_KEY: Record<DayOfWeekKey, DayKey> = {
  "1": "mon",
  "2": "tue",
  "3": "wed",
  "4": "thu",
  "5": "fri",
  "6": "sat",
  "7": "sun",
};

const emptyPlansByDay = (): Record<DayKey, DashboardPlan[]> => ({
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
});

const formatDestination = (lane: DashboardLaneEntry) => {
  const stopCodes = (lane.stops ?? []).map((stop) => stop.airportCode).filter(Boolean);
  const destinationCode = lane.destination?.airportCode ?? "";
  return [...stopCodes, destinationCode].filter(Boolean).join(" → ");
};

const formatTerminalOptionName = (terminal: {
  airportCode?: string;
  terminalName?: string;
}) => {
  const name = terminal.terminalName
    ? terminal.terminalName.toLowerCase().capitalize()
    : "";
  if (terminal.airportCode && name) {
    return `${terminal.airportCode} - ${name}`;
  }
  return terminal.airportCode || name || "";
};

const buildTerminalOptions = (
  terminals: Array<{
    airportCode?: string;
    terminalName?: string;
    terminalNumber?: number | string;
  }>
): XGSSelectOption[] => {
  const byCode = new Map<string, XGSSelectOption>();

  terminals.forEach((terminal) => {
    const code = terminal.airportCode?.trim();
    if (!code || byCode.has(code)) return;

    byCode.set(code, {
      value: code,
      label: formatTerminalOptionName(terminal),
      subtitle:
        terminal.terminalNumber !== undefined && terminal.terminalNumber !== null
          ? String(terminal.terminalNumber)
          : undefined,
    });
  });

  return Array.from(byCode.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label))
  );
};

const TerminalSelectOption = (props: OptionProps<XGSSelectOption>) => (
  <components.Option
    {...props}
    className={`${props.className || ""} xgs-terminal-selector__list__item`.trim()}
  >
    <div className="xgs-terminal-selector__list__item__block">
      <span className="xgs-terminal-selector__list__item__name">
        {props.data.label}
      </span>
      {props.data.subtitle && (
        <span className="xgs-terminal-selector__list__item__id">
          {props.data.subtitle}
        </span>
      )}
    </div>
  </components.Option>
);

const terminalSelectComponents = {
  Option: TerminalSelectOption,
};

const selectMenuStyles = {
  menuPortal: (base: React.CSSProperties) => ({ ...base, zIndex: 1100 }),
};

const formatDepartureTime = (time?: string) => {
  if (!time) return "—";
  return time.slice(0, 5);
};

const mapPlanToDispatch = (
  plan: DashboardPlan,
  origin: string,
  destination: string,
  stops?: string | undefined
): Dispatch => ({
  id: plan.planId,
  airport: destination,
  destination,
  origin,
  stops: stops ?? undefined,
  time: formatDepartureTime(plan.departureTime),
  carrier: plan.carrierName?.trim() || undefined,
  color: plan.carrierNumber === "1325" ? "blue" : "purple",
  carrierRate: String(plan.carrierCost ?? plan.carrierRate ?? 0),
});

const mapDashboardToRows = (dashboardData?: DashboardResponse): RowData[] => {
  if (!dashboardData?.length) return [];

  return dashboardData.map((lane: DashboardLaneEntry, index) => {
    const originCode = lane.origin?.airportCode ?? "";
    const destinationCode = lane.destination?.airportCode ?? "";
    const stopCodes = (lane.stops ?? [])
      .map((stop) => stop.airportCode?.trim())
      .filter((code): code is string => !!code);
    const destination = formatDestination(lane);
    const row: RowData = {
      id: `${originCode}-${destination}-${index}`,
      origin: originCode,
      destination,
      originCode,
      originName: lane.origin?.terminalName ?? "",
      destinationName: lane.destination?.terminalName ?? "",
      destinationCode,
      stopCodes,
      stops: lane.stops?.map((stop) => stop.terminalName || stop.airportCode).filter(Boolean).join(" → "),
      monDispatches: 0,
      tueDispatches: 0,
      wedDispatches: 0,
      thuDispatches: 0,
      friDispatches: 0,
      satDispatches: 0,
      sunDispatches: 0,
      plansByDay: emptyPlansByDay(),
    };

    (Object.keys(DAY_OF_WEEK_TO_FIELD) as DayOfWeekKey[]).forEach((dayKey) => {
      const dayEntry = lane[dayKey];
      if (dayEntry) {
        row[DAY_OF_WEEK_TO_FIELD[dayKey]] = dayEntry.noOfDispatches ?? 0;
        row.plansByDay[DAY_OF_WEEK_TO_DAY_KEY[dayKey]] = dayEntry.plans ?? [];
      }
    });

    return row;
  });
};

const dayConfig: {
  key: DayKey;
  label: string;
  dispatchesField: DispatchField;
}[] = [
    { key: "mon", label: "Monday", dispatchesField: "monDispatches" },
    { key: "tue", label: "Tuesday", dispatchesField: "tueDispatches" },
    { key: "wed", label: "Wednesday", dispatchesField: "wedDispatches" },
    { key: "thu", label: "Thursday", dispatchesField: "thuDispatches" },
    { key: "fri", label: "Friday", dispatchesField: "friDispatches" },
    { key: "sat", label: "Saturday", dispatchesField: "satDispatches" },
    { key: "sun", label: "Sunday", dispatchesField: "sunDispatches" },
  ];

const columnHelper = createColumnHelper<RowData>();

type SelectedCell = {
  rowId: string;
  dayKey: DayKey;
} | null;

type DispatchCellProps = {
  count: number;
  plans: DashboardPlan[];
  origin: string;
  destination: string;
  excluded: boolean;
  showDetails: boolean;
  onToggle: () => void;
  stops?: string | undefined;
  refetchDashboard: () => void;
  readOnly?: boolean;
};

function DispatchCell({
  count,
  plans,
  origin,
  destination,
  excluded,
  stops,
  showDetails,
  onToggle,
  refetchDashboard,
  readOnly = false,
}: DispatchCellProps) {
  const hasPlans = count > 0 && plans.length > 0;
  const isExpanded = hasPlans && showDetails;

  return (
    <div
      className={`dispatchCell${excluded ? " excludedCell" : ""}${isExpanded ? " dispatchCell--expanded" : ""
        }${hasPlans ? " dispatchCell--clickable" : ""}`}
      onClick={hasPlans ? onToggle : undefined}
      role={hasPlans ? "button" : undefined}
      tabIndex={hasPlans ? 0 : undefined}
      onKeyDown={
        hasPlans
          ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onToggle();
            }
          }
          : undefined
      }
    >
      <span>{count}</span>
      {isExpanded && (
        <ul className="dispatchCell__plans">
          {plans.map((plan) => (
            <li
              key={plan.planId}
              className="dispatchCell__planChip"
              onClick={(e) => e.stopPropagation()}
            >
              <DispatchChip
                key={plan.planId}
                dispatch={mapPlanToDispatch(plan, origin, destination, stops)}
                rowLabel={origin}
                refetchLanesByDay={refetchDashboard}
                showDestinationAirport={false}
                readOnly={readOnly}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type SortKey = "origin" | "destination" | "totalDispatches";

type SortState = {
  key: SortKey;
  direction: "asc" | "desc";
};

type StaticLoadPlanTableProps = {
  /** When true, render table/filters only (no page chrome) for embedding in LinehaulScheduler */
  embedded?: boolean;
  readOnly?: boolean;
  /** Controlled region filter when embedded in LinehaulScheduler */
  region?: string;
  onRegionChange?: (region: string) => void;
  regionOptions?: XGSSelectOption[];
};

export default function StaticLoadPlanTable({
  embedded = false,
  readOnly,
  region: controlledRegion,
  onRegionChange,
  regionOptions: controlledRegionOptions,
}: StaticLoadPlanTableProps) {
  const selectedDivision = useSelector((state: IState) => staticLoadSelector(state).selectedDivision);
  const weekStartDate = useSelector((state: IState) => staticLoadSelector(state).weekStartDate);
  const userState = useSelector(userSelector);
  const serviceCentersState = useSelector(serviceCentersSelector);
  const isLinehaulManagementEnabled = UserUtils.hasAssignedGroup(
    userState.profile,
    LINEHAUL_ADMINS_GROUP
  );
  const isReadOnly = readOnly ?? !isLinehaulManagementEnabled;

  let weekEnd = moment(weekStartDate).add(1, 'week').toDate();
  const reduxDispatch = useDispatch();

  const { data: weeks } = useWeeks();

  const [localRegion, setLocalRegion] = useState<string>("");
  const selectedRegion = controlledRegion ?? localRegion;
  const setSelectedRegion = onRegionChange ?? setLocalRegion;

  useEffect(() => {
    // Parent (LinehaulScheduler) owns week selection when embedded
    if (embedded || !weeks) return;
    const latestWeekStartDate = [...weeks]?.sort((a, b) => new Date(b.weekStartDate).getTime() - new Date(a.weekStartDate).getTime())[0]?.weekStartDate;
    reduxDispatch(setSelectedWeekStartDate(latestWeekStartDate));
  }, [weeks, reduxDispatch, embedded])

  useEffect(() => {
    if (!controlledRegionOptions) {
      reduxDispatch(getRegionsServiceCenters());
    }
  }, [reduxDispatch, controlledRegionOptions]);

  const { data: dashboardData, isLoading: isLoadingDashboard, refetch: refetchDashboard } = useDashboard(
    selectedDivision,
    weekStartDate,
    true,
    selectedRegion
  );

  const tableData = useMemo(() => mapDashboardToRows(dashboardData), [dashboardData]);

  const [includedDays, setIncludedDays] = useState<Record<DayKey, boolean>>({
    mon: true,
    tue: true,
    wed: true,
    thu: true,
    fri: true,
    sat: true,
    sun: true,
  });

  const [sort, setSort] = useState<SortState>({
    key: "origin",
    direction: "asc",
  });

  const [showAllDetails, setShowAllDetails] = useState(false);
  const [selectedCell, setSelectedCell] = useState<SelectedCell>(null);
  const [selectedOrigins, setSelectedOrigins] = useState<string[]>([]);
  const [selectedStops, setSelectedStops] = useState<string[]>([]);
  const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);

  const handleShowAllChange = (checked: boolean) => {
    setShowAllDetails(checked);
    if (checked) {
      setSelectedCell(null);
    }
  };

  const toggleSelectedCell = useCallback((rowId: string, dayKey: DayKey) => {
    setSelectedCell((prev) =>
      prev?.rowId === rowId && prev?.dayKey === dayKey
        ? null
        : { rowId, dayKey }
    );
  }, []);

  const regionOptions = useMemo(
    () =>
      controlledRegionOptions ??
      serviceCentersState.regions?.map(
        (region): XGSSelectOption => ({
          label: region.regionName,
          value: region.regionName.toLowerCase(),
        })
      ) ??
      [],
    [controlledRegionOptions, serviceCentersState.regions]
  );

  const originOptions = useMemo<XGSSelectOption[]>(
    () => buildTerminalOptions((dashboardData ?? []).map((lane) => lane.origin)),
    [dashboardData]
  );

  const stopOptions = useMemo<XGSSelectOption[]>(
    () =>
      buildTerminalOptions(
        (dashboardData ?? []).flatMap((lane) => lane.stops ?? [])
      ),
    [dashboardData]
  );

  const destinationOptions = useMemo<XGSSelectOption[]>(
    () =>
      buildTerminalOptions((dashboardData ?? []).map((lane) => lane.destination)),
    [dashboardData]
  );

  useEffect(() => {
    setSelectedOrigins([]);
    setSelectedStops([]);
    setSelectedDestinations([]);
  }, [selectedRegion, weekStartDate]);

  const filteredData = useMemo(() => {
    return tableData.filter((row) => {
      const matchesOrigin =
        selectedOrigins.length === 0 || selectedOrigins.includes(row.originCode);
      const matchesStops =
        selectedStops.length === 0 ||
        selectedStops.some((stop) => row.stopCodes.includes(stop));
      const matchesDestination =
        selectedDestinations.length === 0 ||
        selectedDestinations.includes(row.destinationCode);
      return matchesOrigin && matchesStops && matchesDestination;
    });
  }, [tableData, selectedOrigins, selectedStops, selectedDestinations]);

  const toggleDay = (day: DayKey) => {
    setIncludedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const getRowTotals = useCallback((row: RowData) => {
    return dayConfig.reduce(
      (acc, day) => {
        if (!includedDays[day.key]) {
          return acc;
        }
        acc.dispatches += Number(row[day.dispatchesField] ?? 0);
        return acc;
      },
      {
        dispatches: 0,
      }
    );
  }, [includedDays]);

  /**
   * Custom sort outside TanStack.
   * We sort the data before passing it into useReactTable.
   */

  const sortedData = useMemo(() => {
    const cloned = [...filteredData];

    cloned.sort((a, b) => {
      if (sort.key === "totalDispatches") {
        const aValue = getRowTotals(a).dispatches;
        const bValue = getRowTotals(b).dispatches;
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      const aValue = String(a[sort.key]);
      const bValue = String(b[sort.key]);
      const comparison = aValue.localeCompare(bValue);
      return sort.direction === "asc" ? comparison : -comparison;
    });

    return cloned;
  }, [filteredData, sort, getRowTotals]);

  const handleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });
  };

  const sortLabel = useCallback((key: SortKey) => {
    if (sort.key !== key) return "";
    return sort.direction === "asc" ? <XGSIcon className="sortButton__icon"
      icon={XGSIcons.faChevronDown}
    /> : <XGSIcon className="sortButton__icon"
      icon={XGSIcons.faChevronUp}
    />;
  }, [sort.key, sort.direction]);

  const columns = useMemo<ColumnDef<RowData, any>[]>(
    () => [
      columnHelper.accessor("origin", {
        header: () => (
          <div className="sortButton" onClick={() => handleSort("origin")}>
            Origin{sortLabel("origin")}
          </div>
        ),
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("destination", {
        header: () => (
          <div
            className="sortButton"
            onClick={() => handleSort("destination")}
          >
            Destination{sortLabel("destination")}
          </div>
        ),
        cell: (info) => info.getValue(),
      }),

      columnHelper.group({
        id: "totals",
        header: "",
        columns: [
          columnHelper.display({
            id: "totalDispatches",
            header: () => (
              <div
                className="sortButton"
                onClick={() => handleSort("totalDispatches")}
              >
                Total Dispatches{sortLabel("totalDispatches")}
              </div>
            ),
            cell: ({ row }) => getRowTotals(row.original).dispatches,
          }),
        ],
      }),

      ...dayConfig.map((day) =>
        columnHelper.group({
          id: day.key,
          header: () => (
            <label className="groupHeaderCheckbox">
              <input
                type="checkbox"
                checked={includedDays[day.key]}
                onChange={() => toggleDay(day.key)}
              />
              <span>{day.label}</span>
            </label>
          ),
          columns: [
            columnHelper.accessor(day.dispatchesField, {
              id: `${day.key}Dispatches`,
              header: "Dispatches",
              cell: (info) => {
                const rowId = info.row.original.id;
                const isSelected =
                  selectedCell?.rowId === rowId &&
                  selectedCell?.dayKey === day.key;

                return (
                  <DispatchCell
                    count={Number(info.getValue() ?? 0)}
                    plans={info.row.original.plansByDay[day.key] ?? []}
                    origin={info.row.original.originName}
                    destination={info.row.original.destinationName}
                    stops={info.row.original.stops}
                    excluded={!includedDays[day.key]}
                    showDetails={showAllDetails || isSelected}
                    onToggle={() => toggleSelectedCell(rowId, day.key)}
                    refetchDashboard={refetchDashboard}
                    readOnly={isReadOnly}
                  />
                );
              },
            }),
          ],
        })
      ),
    ],
    [
      includedDays,
      getRowTotals,
      sortLabel,
      showAllDetails,
      selectedCell,
      toggleSelectedCell,
      refetchDashboard,
      isReadOnly,
    ]
  );

  const table = useReactTable({
    data: sortedData,
    columns,
    getCoreRowModel: getCoreRowModel(),

    /**
     * Important:
     * Do not add getSortedRowModel here because sorting is custom.
     */
  });

  const grandTotals = useMemo(() => {
    return sortedData.reduce(
      (acc, row) => {
        const totals = getRowTotals(row);
        acc.dispatches += totals.dispatches;
        return acc;
      },
      {
        dispatches: 0,
      }
    );
  }, [sortedData, getRowTotals]);

  const isDayColumn = (columnId: string) =>
    dayConfig.some(
      (day) => columnId === day.key || columnId === `${day.key}Dispatches`
    );

  const getStickyClass = (columnId: string) => {
    switch (columnId) {
      case "origin":
        return "stickyCol stickyCol--origin";
      case "destination":
        return "stickyCol stickyCol--destination";
      case "totals":
      case "totalDispatches":
        return "stickyCol stickyCol--totalDispatches";
      default:
        return "";
    }
  };

  const cellClassName = (columnId: string) =>
    [isDayColumn(columnId) ? "dispatchCol" : "", getStickyClass(columnId)]
      .filter(Boolean)
      .join(" ") || undefined;

  const showAllToggle = (
    <XGSToggle
      checked={showAllDetails}
      onChange={handleShowAllChange}
      label="Show All"
    />
  );

  const dashboardBody = (
    <>
      {!embedded && weekStartDate && (
        <div className="week-navigator-title">
          You are viewing the schedule for {moment(weekStartDate).format("MMMM D, YYYY")} -{" "}
          {moment(weekEnd).format("MMMM D, YYYY")}. Changes apply only to this week.
        </div>
      )}
      <div className="dashboardContainer">
        <Formik
          enableReinitialize={false}
          initialValues={{
            region: selectedRegion,
            origins: selectedOrigins,
            stops: selectedStops,
            destinations: selectedDestinations,
          }}
          onSubmit={() => undefined}
        >
          <div className="dashboardFilter">
            <XGSFormSelect
              name="region"
              label="Region"
              labelMode={LabelModes.column}
              className="dashboardFilter__select"
              options={regionOptions}
              isClearable
              controlled
              placeholder="All regions"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
              value={regionOptions.find((option) => String(option.value) === selectedRegion) ?? null}
              onValueChange={(option) => {
                setSelectedRegion(option ? String(option.value) : "");
              }}
            />
            <XGSFormSelect
              name="origins"
              label="Origin"
              labelMode={LabelModes.column}
              className="dashboardFilter__select"
              options={originOptions}
              isMulti
              isClearable
              controlled
              placeholder="All origins"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
              components={terminalSelectComponents}
              value={originOptions.filter((option) =>
                selectedOrigins.includes(String(option.value))
              )}
              onMultiValuesChange={(selected) => {
                setSelectedOrigins(selected?.map((item) => String(item.value)) ?? []);
              }}
            />
            <XGSFormSelect
              name="stops"
              label="Stop"
              labelMode={LabelModes.column}
              className="dashboardFilter__select"
              options={stopOptions}
              isMulti
              isClearable
              controlled
              placeholder="All stops"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
              components={terminalSelectComponents}
              value={stopOptions.filter((option) =>
                selectedStops.includes(String(option.value))
              )}
              onMultiValuesChange={(selected) => {
                setSelectedStops(selected?.map((item) => String(item.value)) ?? []);
              }}
            />
            <XGSFormSelect
              name="destinations"
              label="Destination"
              labelMode={LabelModes.column}
              className="dashboardFilter__select"
              options={destinationOptions}
              isMulti
              isClearable
              controlled
              placeholder="All destinations"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
              components={terminalSelectComponents}
              value={destinationOptions.filter((option) =>
                selectedDestinations.includes(String(option.value))
              )}
              onMultiValuesChange={(selected) => {
                setSelectedDestinations(
                  selected?.map((item) => String(item.value)) ?? []
                );
              }}
            />
            {embedded && (
              <div className="dashboardFilter__showAll">{showAllToggle}</div>
            )}
          </div>
        </Formik>

        <div className="tableScrollWrapper">
          {isLoadingDashboard ? (
            <Loading isLoading={isLoadingDashboard} />
          ) : (
            <table className="loadPlanTable">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          className={cellClassName(header.column.id)}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          className={cellClassName(cell.column.id)}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                <tr className="grandTotalRow">
                  <td className="stickyCol stickyCol--origin">Total</td>
                  <td className="stickyCol stickyCol--destination"></td>
                  <td className="grandTotalCell stickyCol stickyCol--totalDispatches">
                    {grandTotals.dispatches}
                  </td>
                  {dayConfig.map((day) => (
                    <React.Fragment key={day.key}>
                      <td
                        className={`dispatchCol${
                          includedDays[day.key] ? "" : " excludedCell"
                        }`}
                      >
                        {includedDays[day.key]
                          ? sortedData.reduce(
                              (sum, row) =>
                                sum + Number(row[day.dispatchesField] ?? 0),
                              0
                            )
                          : "-"}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );

  if (embedded) {
    return dashboardBody;
  }

  return (
    <ContentContainer
      mods={{ "full-width": true }}
      titleComponent={
        <ContentContainerToolbar title="Static Load Dashboard" mods={{ "full-width": true }}>
          {showAllToggle}
        </ContentContainerToolbar>
      }
    >
      {dashboardBody}
    </ContentContainer>
  );
}